import { Box, Chip, Grid, IconButton } from "@material-ui/core";
import { blue, green, grey, orange, red } from "@material-ui/core/colors";
import { Comment, Edit, Info, RateReview } from "@material-ui/icons";
import {
    Championship,
    Collections,
    Incident,
    IncidentOriginDescription,
    IncidentReviewRequest,
    Infraction,
    isIncidentApplied,
    PenaltyDescriptionByType,
    Protest,
    RaceControl,
    Role,
    Round,
    StatusIncident,
    StatusIncidentColor,
    StatusIncidentDescription,
    StatusIncidentReviewRequestColor,
    StatusIncidentReviewRequestDescription,
    useChampionshipDrivers,
    useCollection,
    useIncidentReviewRequests,
    useIncidents,
    useInvolvedProtests,
} from "api";
import { functions } from "config/firebaseConfig";
import { CsvBuilder } from "filefy";
import { DateTime } from "luxon";
import MaterialTable from "material-table";
import React, { useContext, useMemo, useState } from "react";
import { BoxCard, LoggedUserContext } from "ui/componentes";
import { IconGavel } from "ui/componentes/Icons";
import { useIsValidRoundToProtest } from "ui/views";
import { CarsNumbersToChip } from "ui/views/ChampionshipForm/RoundEdit/CarsNumbersToChip";
import { IncidentReportProtest } from "ui/views/IncidentsReportView/IncidentReportProtest";
import { IncidentReviewRequestDialog } from "ui/views/IncidentsReportView/IncidentReviewRequestDialog";
import { IncidentReviewRequestInfoDialog } from "ui/views/IncidentsReportView/IncidentReviewRequestInfoDialog";
import { ProtestInfoDialog } from "ui/views/IncidentsReportView/ProtestInfoDialog";
import { LIMIT_OF_DAYS_TO_PROTEST } from "ui/views/Protests";
import { LocalizationPTBr } from "util/MaterialTableUtil";

const exportToCsv = (raceControl, driversByNumber, infractions) => (
    columns,
    data
) => {
    const fileName = `bateria_${
        raceControl.raceNumber
    }_${DateTime.local().toFormat("dd-MM-yyyy-HH-mm-ss")}.csv`;

    const titleColumns = columns
        .filter((c) => c.export === undefined || c.export)
        .map((c) => c.title);

    const rows = data.map((incident) => {
        const row: string[] = [];

        row.push(incident.lap);

        if (incident.involvedCars) {
            row.push(
                incident.involvedCars
                    .map((carNumber) => driversByNumber[carNumber])
                    .join(", ")
            );
        } else {
            row.push("-");
        }

        if (incident.guiltyCar && incident.guiltyCar !== "-1") {
            row.push(driversByNumber[incident.guiltyCar]);
        } else {
            row.push("-");
        }

        if (incident.infractionId) {
            const infraction: Infraction = infractions[incident.infractionId];
            const penaltyPoints =
                infraction.penaltyPoints && infraction.penaltyPoints > 0
                    ? ` - (${infraction.penaltyPoints} PP)`
                    : "";
            const appliedAfterRace =
                incident.status === StatusIncident.APPLIED_AFTER_RACE
                    ? `(${
                          StatusIncidentDescription[
                              StatusIncident.APPLIED_AFTER_RACE
                          ]
                      })`
                    : "";

            const infractionData = `${infraction.key} - ${
                infraction.description
            } (${
                PenaltyDescriptionByType[infraction.penaltyType || ""]
            })${penaltyPoints} ${appliedAfterRace}`;

            row.push(infractionData);
        } else {
            row.push(StatusIncidentDescription[incident.status]);
        }

        return row;
    });

    new CsvBuilder(fileName)
        .setDelimeter(";")
        .setColumns(titleColumns)
        .addRows(rows)
        .exportFile();
};

const statusDescription = (status: StatusIncident) => {
    if (
        status === StatusIncident.INVESTIGATING ||
        status === StatusIncident.AFTER_RACE ||
        status === StatusIncident.NOTED
    ) {
        return StatusIncidentDescription[StatusIncident.INVESTIGATING];
    }
    return StatusIncidentDescription[status];
};

const colorStatus = (status: StatusIncident) => {
    let statusToColor = status;
    if (
        status === StatusIncident.INVESTIGATING ||
        status === StatusIncident.AFTER_RACE ||
        status === StatusIncident.NOTED
    ) {
        statusToColor = StatusIncident.INVESTIGATING;
    }
    return StatusIncidentColor[statusToColor];
};

interface ProtestInfoState {
    protest?: Protest;
    showDialog: boolean;
}

interface IncidentsReportRaceProps {
    raceControl: RaceControl;
    round: Round;
    championship: Championship;
}

const IncidentsReportRace: React.FC<IncidentsReportRaceProps> = ({
    raceControl,
    round,
    championship,
}) => {
    const loggedUser = useContext(LoggedUserContext);

    const [openProtest, setOpenProtest] = useState(false);

    const [requestReviewState, setRequestReviewState] = useState<{
        open: boolean;
        incident?: Incident;
    }>({ open: false });

    const [requestReviewInfoState, setRequestReviewInfoState] = useState<{
        open: boolean;
        requestReview?: IncidentReviewRequest;
    }>({ open: false });

    const infractions =
        useCollection<object>({
            collection: Collections.INFRACTIONS,
            storeAs: "infractionsToReport",
        }) || {};

    const incidents = useIncidents(raceControl.id);
    const involvedProtests = useInvolvedProtests({
        userId: loggedUser.id || "-1",
        roundId: raceControl.roundId,
    });
    const involvedProtestsId = useMemo(() => {
        return involvedProtests.map((protest) => protest.id);
    }, [involvedProtests]);
    const [protestInfoState, setProtestInfoState] = useState<ProtestInfoState>({
        showDialog: false,
    });

    const drivers = useChampionshipDrivers(championship?.id);
    const [driversByNumber, myDriverNumber] = useMemo(() => {
        const driversByNumber = {};
        let myDriverNumber = "";
        drivers.forEach((driver) => {
            driversByNumber[
                driver.usedNumber
            ] = `#${driver.usedNumber} - ${driver.displayName}`;
            if (driver.userId === loggedUser.id) {
                myDriverNumber = driver.usedNumber;
            }
        });
        return [driversByNumber, myDriverNumber];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drivers]);

    const isRoundValidToProtest = useIsValidRoundToProtest(round);
    const incidentReviewRequests = useIncidentReviewRequests({
        userId: loggedUser.roles.includes(Role.ADMIN) ? "" : loggedUser.id,
        roundId: round.id || "",
    });
    const incidentReviewRequestsByIncidentId = useMemo(() => {
        const incidentReviewRequestsByIncidentId = {};
        incidentReviewRequests.forEach(
            (review) =>
                (incidentReviewRequestsByIncidentId[review.incidentId] = review)
        );
        return incidentReviewRequestsByIncidentId;
    }, [incidentReviewRequests]);

    const canRequestIncidentReview = useMemo(() => {
        const todayMinusLimitToProtest = DateTime.local()
            .minus({ day: LIMIT_OF_DAYS_TO_PROTEST })
            .toFormat("yyyy-MM-dd");

        return todayMinusLimitToProtest <= round.date;
    }, [round]);

    return (
        <>
            <IncidentReportProtest
                open={openProtest}
                onClose={() => setOpenProtest(false)}
                championship={championship}
                raceControl={raceControl}
            />
            <Grid container spacing={1}>
                <ProtestInfoDialog
                    show={protestInfoState.showDialog}
                    protest={protestInfoState.protest}
                    driversByNumber={driversByNumber}
                    onClose={() =>
                        setProtestInfoState({
                            showDialog: false,
                            protest: undefined,
                        })
                    }
                />
                <IncidentReviewRequestDialog
                    open={requestReviewState.open}
                    onClose={() => setRequestReviewState({ open: false })}
                    onSave={(description) => {
                        const incidentReviewRequest = {
                            description,
                            championshipId: championship.id || "",
                            roundId: round.id || "",
                            raceControlId: raceControl.id || "",
                            incidentId: requestReviewState.incident?.id || "",
                        };

                        return functions
                            .httpsCallable("protest-incidentReviewRequest")(
                                incidentReviewRequest
                            )
                            .finally(() =>
                                setRequestReviewState({ open: false })
                            );
                    }}
                />
                <IncidentReviewRequestInfoDialog
                    open={requestReviewInfoState.open}
                    onClose={() => setRequestReviewInfoState({ open: false })}
                    incidentReviewRequest={requestReviewInfoState.requestReview}
                />
                <Grid item xs={12}>
                    <MaterialTable
                        title={`Bateria ${raceControl.raceNumber}`}
                        components={{
                            Container: BoxCard,
                        }}
                        data={incidents}
                        columns={[
                            {
                                field: "lap",
                                title: "Volta",
                                width: "5%",
                                align: "right",
                            },
                            {
                                field: "origin",
                                title: "Origem",
                                width: "7%",
                                align: "center",
                                export: false,
                                render: (incident) => {
                                    const showInfoIcon =
                                        incident.protestId &&
                                        involvedProtestsId.includes(
                                            incident.protestId
                                        );
                                    let protest;
                                    if (incident.protestId) {
                                        const protests = involvedProtests.filter(
                                            (protest) =>
                                                protest.id ===
                                                incident.protestId
                                        );
                                        if (protests && protests.length > 0) {
                                            protest = protests[0];
                                        }
                                    }

                                    return (
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            {
                                                IncidentOriginDescription[
                                                    incident.origin
                                                ]
                                            }
                                            {showInfoIcon ? (
                                                <IconButton
                                                    style={{ padding: 4 }}
                                                    onClick={() =>
                                                        setProtestInfoState({
                                                            protest,
                                                            showDialog: true,
                                                        })
                                                    }
                                                    title="Ver protesto"
                                                >
                                                    {protest.protestedUserId ===
                                                        loggedUser.id &&
                                                    !protest.defense ? (
                                                        <Edit />
                                                    ) : (
                                                        <IconGavel size="sm" />
                                                    )}
                                                </IconButton>
                                            ) : null}
                                        </Box>
                                    );
                                },
                            },
                            {
                                field: "involvedCars",
                                title: "Envolvidos",
                                render: (incident) => {
                                    if (incident.involvedCars) {
                                        return (
                                            <CarsNumbersToChip
                                                carNumbers={
                                                    incident.involvedCars
                                                }
                                                driversByNumber={
                                                    driversByNumber
                                                }
                                                color="primary"
                                                style={{ width: "170px" }}
                                            />
                                        );
                                    }
                                    return "";
                                },
                            },
                            {
                                field: "guiltyCar",
                                title: "Culpado",
                                width: "20%",
                                render: (incident) => {
                                    if (incident.guiltyCar) {
                                        return (
                                            <CarsNumbersToChip
                                                carNumbers={
                                                    incident.guiltyCar === "-1"
                                                        ? []
                                                        : [incident.guiltyCar]
                                                }
                                                driversByNumber={
                                                    driversByNumber
                                                }
                                                style={{
                                                    backgroundColor: red[500],
                                                    color: "white",
                                                    width: "170px",
                                                }}
                                            />
                                        );
                                    }
                                    return "";
                                },
                            },
                            {
                                field: "infractionId",
                                title: "Infração",
                                align: "center",
                                render: (incident) => {
                                    if (
                                        incident.status ===
                                        StatusIncident.NO_FURTHER_ACTION
                                    )
                                        return "";
                                    if (incident.infractionId) {
                                        const infraction: Infraction =
                                            infractions[incident.infractionId];

                                        if (!infraction) {
                                            return "";
                                        }

                                        let appliedAfterMessage = "";
                                        if (
                                            infraction.penaltyPointsAppliedAfter >
                                            1
                                        ) {
                                            appliedAfterMessage = `após ${infraction.penaltyPointsAppliedAfter} ocorrências`;
                                        }

                                        const penaltyPoints =
                                            infraction &&
                                            infraction.penaltyPoints &&
                                            infraction.penaltyPoints > 0
                                                ? ` - (${infraction.penaltyPoints} PP ${appliedAfterMessage})`
                                                : "";
                                        return `${infraction.key} - ${
                                            infraction.description
                                        } (${
                                            PenaltyDescriptionByType[
                                                infraction.penaltyType || ""
                                            ]
                                        })${penaltyPoints}`;
                                    }
                                    return "";
                                },
                            },
                            {
                                field: "status",
                                title: "Status",
                                align: "center",
                                width: "210px",
                                export: false,
                                render: (incident) => (
                                    <Chip
                                        label={statusDescription(
                                            incident.status
                                        )}
                                        style={{
                                            ...colorStatus(incident.status),
                                            width: "175px",
                                        }}
                                        size="small"
                                    />
                                ),
                            },
                        ]}
                        style={{ padding: 0 }}
                        options={{
                            draggable: false,
                            sorting: false,
                            search: false,
                            paging: false,
                            rowStyle: (incident) => ({
                                padding: 0,
                                fontSize: "0.857rem",
                                backgroundColor: incident.protestId
                                    ? blue["50"]
                                    : "",
                            }),
                            exportButton: loggedUser.roles.includes(Role.ADMIN),
                            exportDelimiter: ";",
                            exportCsv: exportToCsv(
                                raceControl,
                                driversByNumber,
                                infractions
                            ),
                        }}
                        actions={[
                            {
                                icon: () => (
                                    <IconGavel
                                        size="sm"
                                        style={{
                                            color: isRoundValidToProtest
                                                ? orange["500"]
                                                : grey["400"],
                                        }}
                                    />
                                ),
                                onClick: () => setOpenProtest(true),
                                isFreeAction: true,
                                tooltip: isRoundValidToProtest
                                    ? "Abrir protesto"
                                    : "Prazo para protesto encerrado",
                                disabled: !isRoundValidToProtest,
                            },
                            (incident) => {
                                const incidentReviewRequest =
                                    incidentReviewRequestsByIncidentId[
                                        incident.id || ""
                                    ];

                                return {
                                    icon: () => (
                                        <RateReview
                                            style={{
                                                color: green["500"],
                                            }}
                                        />
                                    ),
                                    hidden:
                                        !!incident.protestId ||
                                        incident.guiltyCar !== myDriverNumber ||
                                        !isIncidentApplied(incident) ||
                                        incident.status ===
                                            StatusIncident.NO_FURTHER_ACTION ||
                                        !!incidentReviewRequest ||
                                        !canRequestIncidentReview,
                                    onClick: () => {
                                        setRequestReviewState({
                                            open: true,
                                            incident,
                                        });
                                    },
                                    tooltip:
                                        "Solicitar reavalição de penalidade",
                                };
                            },
                            (incident) => {
                                const incidentReviewRequest =
                                    incidentReviewRequestsByIncidentId[
                                        incident.id || ""
                                    ];

                                return {
                                    icon: () => (
                                        <Comment
                                            style={{
                                                color:
                                                    StatusIncidentReviewRequestColor[
                                                        incidentReviewRequest?.status ||
                                                            ""
                                                    ],
                                            }}
                                        />
                                    ),
                                    hidden: !incidentReviewRequest,
                                    onClick: () => {
                                        setRequestReviewInfoState({
                                            open: true,
                                            requestReview: incidentReviewRequest,
                                        });
                                    },
                                    tooltip: `Pedido de Revisão ${
                                        StatusIncidentReviewRequestDescription[
                                            incidentReviewRequest?.status || ""
                                        ]
                                    }`,
                                };
                            },
                            (incident) => {
                                return {
                                    icon: () => (
                                        <Info style={{ color: grey["500"] }} />
                                    ),
                                    hidden: !incident.comment,
                                    tooltip: incident.comment,
                                    onClick: () => {},
                                };
                            },
                        ]}
                        localization={LocalizationPTBr}
                    />
                </Grid>
            </Grid>
        </>
    );
};

export { IncidentsReportRace };
